import { RentPayment } from '../../types/properties';
import { formatCurrency } from '../../lib/format';
import { format } from 'date-fns';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';

interface RentTrackerProps {
  payments: RentPayment[];
}

export function RentTracker({ payments }: RentTrackerProps) {
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'paid': return <CheckCircle2 size={16} className="text-[var(--t-success)]" />;
      case 'pending': return <Clock size={16} className="text-[var(--t-warning)]" />;
      case 'late': return <AlertCircle size={16} className="text-[var(--t-error)]" />;
      default: return null;
    }
  };

  const sortedPayments = [...payments].sort((a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime());

  if (payments.length === 0) {
    return <div className="text-sm text-[var(--t-text-muted)] italic text-center p-8 border border-dashed border-[var(--t-border)] rounded-xl">No payment history found.</div>;
  }

  return (
    <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-[var(--t-surface-hover)] border-b border-[var(--t-border)]">
          <tr>
            <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Date</th>
            <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Amount</th>
            <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Method</th>
            <th className="px-4 py-3 font-semibold text-[var(--t-text-muted)]">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--t-border-subtle)]">
          {sortedPayments.map(payment => (
            <tr key={payment.id} className="hover:bg-[var(--t-surface-hover)]/50 transition-colors">
              <td className="px-4 py-3 text-white">
                {format(new Date(payment.payment_date), 'MMM d, yyyy')}
              </td>
              <td className="px-4 py-3 text-[var(--t-text)] font-medium">
                {formatCurrency(payment.amount)}
              </td>
              <td className="px-4 py-3 text-[var(--t-text-muted)] capitalize">
                {payment.payment_method?.replace('-', ' ') || 'Unknown'}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(payment.status)}
                  <span className="capitalize text-[var(--t-text)] font-medium">{payment.status}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
